import { useCallback, useEffect, useRef, useState } from "react";
import type { VideoDetailDto, VideoKey, VideoProgressResponse, VideoResponse } from "../shared/dto";
import {
  actionIssueFrom,
  actionIssueFromResponse,
  actionIssueFromThrown,
  actionIssueLocaleKey,
  actionIssuePermanent,
  readErrorCode,
  type ActionIssue
} from "./action-error";
import { READONLY_BLOCKED_ERROR } from "./readonly";


// F81 (Pavel 2026-08-06): the gated video player IS the step - the group
// calls are gone. First viewing is watch-through: pause and seeking BACK
// always work, seeking FORWARD past the watched edge waits until the video
// was seen once. The next step opens at the video's unlock moment
// (unlockAtSeconds, server truth); the continue button then appears inside
// the player as an event. After full completion seeking is free. The server
// owns the watch state, so progress survives reloads and devices; the player
// reports every ~10 s, on pause, tab hide, video end, and the moment playback
// crosses the unlock threshold (so the button appears when Pavel says
// "prave ted ti oteviram dalsi krok", not ten seconds later).

export const PROGRESS_POST_INTERVAL_MS = 10_000;
// A small grace past the watched edge keeps honest scrubbing near the edge
// from feeling sticky; it is no shortcut (the server clamp still applies).
export const FORWARD_SEEK_TOLERANCE_SECONDS = 1.5;

export function clampSeekTarget(input: {
  target: number;
  maxWatched: number;
  completed: boolean;
  duration: number;
}): { time: number; blocked: boolean } {
  const upper = Number.isFinite(input.duration) && input.duration > 0 ? input.duration : Math.max(input.target, 0);
  const target = Math.min(Math.max(input.target, 0), upper);
  if (input.completed) return { time: target, blocked: false };
  const limit = Math.min(input.maxWatched + FORWARD_SEEK_TOLERANCE_SECONDS, upper);
  if (target <= limit) return { time: target, blocked: false };
  return { time: limit, blocked: true };
}

export function formatTime(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function playedPct(current: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return Math.max(0, Math.min(100, (current / duration) * 100));
}

/**
 * F150: what the player does with a REFUSED watch report.
 *
 * Before this, a non-ok answer was a bare `return`, and that is where the
 * whole fault hid: under "Otevrit jako klient" the browser gate refused every
 * report with a 403 nothing ever read, so the stored seconds never grew, the
 * unlock threshold never arrived and the band never appeared - under a player
 * that looked perfectly healthy. Silence is a lie whenever the thing that
 * failed is invisible.
 *
 * Two answers, because there are two questions:
 *  - `halt`: is repeating this worth anything? A refusal a repeat cannot clear
 *    is a request the server will keep saying no to, every ten seconds.
 *  - `issue`: does the PLAYER owe the viewer a sentence? Not for the read-only
 *    gate: the impersonation bar already names that refusal and offers the
 *    switch, and one refusal has one home (the F138 law). For everything else
 *    nothing on the screen says it, so the player must.
 *
 * 409 never reaches here: an implausible report is answered by re-reading the
 * server's own state, which is a real fix rather than a refusal.
 */
export function progressReportRefusal(status: number, code: string | null): {
  halt: boolean;
  issue: ActionIssue | null;
} {
  if (code === READONLY_BLOCKED_ERROR) return { halt: true, issue: null };
  const issue = actionIssueFromResponse(status, code);
  return actionIssuePermanent(issue) ? { halt: true, issue } : { halt: false, issue: null };
}

interface VideoRuntime {
  currentTime: number;
  duration: number;
  playing: boolean;
  muted: boolean;
  volume: number;
  fullscreen: boolean;
  lockHint: boolean;
  showResumeNote: boolean;
}

/**
 * F81: the honest "the step is a video and the video is not here yet" card.
 * Rendered whenever a call step has no playable video configured; the same
 * fact puts a "video missing" service row into the partner's queue, so the
 * card never lies about who the ball is with.
 */
export function VideoPreparingCard(props: { t: (key: string) => string }) {
  return (
    <div className="video-gate video-gate-preparing" data-video-preparing="true">
      <div className="video-preparing-frame" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path fill="currentColor" d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4Z" /></svg>
      </div>
      <div className="video-preparing-body">
        <strong>{props.t("video.preparingTitle")}</strong>
        <p>{props.t("video.preparingCopy")}</p>
      </div>
    </div>
  );
}

// Presentational surface: everything through props so the SSR component tests
// can assert the locked, unlocked, completed and resume states without a
// media element.
export function VideoSurface(props: {
  video: Pick<VideoDetailDto, "title" | "maxWatchedSeconds" | "completed" | "unlockAtSeconds">;
  runtime: VideoRuntime;
  unlockReady: boolean;
  /** F115: the media element itself refused the file (moved, expired signed
      URL, blocked network). The player looks perfectly healthy in that state,
      so the surface has to SAY it. */
  mediaFailed?: boolean;
  onReloadMedia?: () => void;
  /** F150: the server refuses the watch REPORTS. The video plays, the bar
      fills, and not one of those seconds is stored - so the unlock moment can
      never arrive. The same class of lie as `mediaFailed`, one layer down. */
  progressIssue?: ActionIssue | null;
  t: (key: string) => string;
  onTogglePlay: () => void;
  onSeek: (clientX: number, bar: HTMLElement) => void;
  onSeekBy: (deltaSeconds: number) => void;
  onToggleMute: () => void;
  onVolume: (value: number) => void;
  onToggleFullscreen: () => void;
  onContinue: () => void;
  stageRef?: React.Ref<HTMLDivElement>;
  children?: React.ReactNode;
}) {
  const { runtime, video } = props;
  const duration = runtime.duration;
  const watchedEdgePct = video.completed ? 100 : playedPct(video.maxWatchedSeconds, duration);
  // The unlock moment drawn on the bar: the story marker of "here the next
  // step opens". Hidden once the video is completed (the bar is free then).
  const unlockPct = !video.completed && video.unlockAtSeconds !== null && duration > 0
    ? Math.min(100, (video.unlockAtSeconds / duration) * 100)
    : null;
  return (
    <div className={`video-gate${video.completed ? " is-completed" : ""}${props.unlockReady ? " is-unlocked" : ""}`}>
      {video.title && <p className="video-title">{video.title}</p>}
      {/* The unlock band tells the state once it exists; a second copy of the
          same sentence above the stage read as an echo (F81 shot run 2). */}
      {!props.unlockReady && (
        <p className="video-gate-note">{props.t(video.completed ? "video.gateNoteDone" : "video.gateNote")}</p>
      )}
      {/* F115 (audit finding 3): the FIRST step of the journey used to answer a
          broken file with nothing at all - the play button did nothing, no
          text changed, and the client had no way to tell whose fault it was.
          The message names what happened, says the fault is ours, offers the
          one repeat that can actually help (re-loading the media) and points
          at the partner. It sits ABOVE the player: the player itself looks
          perfectly healthy, so the sentence must not hide below it. */}
      {props.mediaFailed && (
        <div className="video-media-error" data-video-media-error="true" role="alert">
          <strong>{props.t("video.mediaErrorTitle")}</strong>
          <p>{props.t("video.mediaErrorCopy")}</p>
          {props.onReloadMedia && (
            <button className="secondary-button compact-button" type="button" onClick={props.onReloadMedia}>
              {props.t("video.mediaErrorRetry")}
            </button>
          )}
        </div>
      )}
      {/* F150: the report was refused for a reason repeating cannot clear, so
          the player has stopped sending them. Above the stage for the same
          reason the media error is: from below the player nothing looks wrong.
          The gate's own refusal ("jen koukam") is NOT drawn here - the
          impersonation bar names that one, and one sentence has one home. */}
      {props.progressIssue && (
        <div className="video-media-error" data-video-progress-error="true" role="alert">
          <strong>{props.t("video.progressBlockedTitle")}</strong>
          <p>{props.t(actionIssueLocaleKey(props.progressIssue))}</p>
        </div>
      )}
      <div className="video-stage" ref={props.stageRef} data-video-stage="true">
        {props.children}
        {!runtime.playing && (
          <button
            className="video-center-play"
            data-video-center-play="true"
            type="button"
            onClick={props.onTogglePlay}
            aria-label={props.t("video.play")}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z" /></svg>
          </button>
        )}
        {runtime.lockHint && !video.completed && (
          <p className="video-lock-hint" data-video-lock-hint="true" role="status">{props.t("video.seekLocked")}</p>
        )}
        <div className="video-chrome">
          <button className="video-control-button" type="button" onClick={props.onTogglePlay}
            aria-label={props.t(runtime.playing ? "video.pause" : "video.play")}>
            {runtime.playing ? (
              <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z" /></svg>
            )}
          </button>
          <span className="video-time">{formatTime(runtime.currentTime)}<small> / {formatTime(duration)}</small></span>
          <div
            className={`video-bar${video.completed ? " video-bar-free" : ""}`}
            data-video-bar="true"
            role="slider"
            tabIndex={0}
            aria-label={props.t("video.progressLabel")}
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(runtime.currentTime)}
            aria-valuetext={`${formatTime(runtime.currentTime)} / ${formatTime(duration)}`}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              props.onSeek(event.clientX, event.currentTarget);
            }}
            onPointerMove={(event) => {
              if (event.buttons === 1) props.onSeek(event.clientX, event.currentTarget);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft") { event.preventDefault(); props.onSeekBy(-5); }
              if (event.key === "ArrowRight") { event.preventDefault(); props.onSeekBy(5); }
              if (event.key === " " || event.key === "Enter") { event.preventDefault(); props.onTogglePlay(); }
            }}
          >
            <div className="video-bar-watched" style={{ width: `${watchedEdgePct}%` }} />
            <div className="video-bar-played" style={{ width: `${playedPct(runtime.currentTime, duration)}%` }} />
            {unlockPct !== null && (
              <span
                className={`video-bar-unlockmark${props.unlockReady ? " is-passed" : ""}`}
                data-video-unlock-mark="true"
                style={{ left: `${unlockPct}%` }}
                aria-hidden="true"
              />
            )}
            {!video.completed && (
              <span className="video-bar-lock" data-video-lock="true" style={{ left: `${watchedEdgePct}%` }} aria-hidden="true">
                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm-3 8V7a3 3 0 1 1 6 0v3Z" /></svg>
              </span>
            )}
          </div>
          <button className="video-control-button" type="button" onClick={props.onToggleMute}
            aria-label={props.t(runtime.muted ? "video.unmute" : "video.mute")}>
            {runtime.muted ? (
              <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4 9v6h4l5 5V4L8 9H4zm12.6 3 2.7 2.7-1.4 1.4-2.7-2.7-2.7 2.7-1.4-1.4 2.7-2.7-2.7-2.7 1.4-1.4 2.7 2.7 2.7-2.7 1.4 1.4-2.7 2.7z" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4 9v6h4l5 5V4L8 9H4zm12.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" /></svg>
            )}
          </button>
          <input
            className="video-volume"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={runtime.muted ? 0 : runtime.volume}
            aria-label={props.t("video.volume")}
            onChange={(event) => props.onVolume(Number(event.target.value))}
          />
          <button className="video-control-button" type="button" onClick={props.onToggleFullscreen}
            aria-label={props.t(runtime.fullscreen ? "video.exitFullscreen" : "video.fullscreen")}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
          </button>
        </div>
      </div>
      <div className="video-under-row" aria-live="polite">
        {props.unlockReady ? (
          // The unlock is an EVENT: the band slides in the moment the server
          // confirms the threshold, and the one amber button leads straight
          // to the step the video just opened.
          <div className="video-unlock-band" data-video-unlock-band="true">
            <span className="video-unlock-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a5 5 0 0 1 5 5v3h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h8V7a3 3 0 0 0-6 0H6a5 5 0 0 1 6-5Z" /></svg>
            </span>
            <div className="video-unlock-copy">
              <strong>{props.t(video.completed ? "video.gateNoteDone" : "video.unlockOpenedTitle")}</strong>
              {!video.completed && <p>{props.t("video.unlockOpenedCopy")}</p>}
              {video.completed && (
                <p className="video-completed-row">
                  <span className="video-completed-badge" data-video-completed="true">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" /></svg>
                    {props.t("video.completedBadge")}
                  </span>
                  {props.t("video.freeSeekNote")}
                </p>
              )}
            </div>
            <button className="primary-button video-continue" data-video-continue="true" type="button" onClick={props.onContinue}>
              {props.t("video.continue")}
            </button>
          </div>
        ) : video.completed ? (
          // Completed but the step did not open here (historic call unlock,
          // test walkthrough): the badge tells the truth, no dead button.
          <p className="video-completed-row">
            <span className="video-completed-badge" data-video-completed="true">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" /></svg>
              {props.t("video.completedBadge")}
            </span>
            {props.t("video.freeSeekNote")}
          </p>
        ) : runtime.showResumeNote ? (
          <p className="video-resume-note">{props.t("video.resumeNote")}</p>
        ) : (
          <p className="video-first-note">{props.t("video.firstWatchNote")}</p>
        )}
      </div>
    </div>
  );
}

export function VideoGate(props: {
  videoKey: VideoKey;
  csrfToken: string;
  t: (key: string) => string;
  /** The continue button: refresh state and lead the client into the step
      the video opened. Never called automatically - the video keeps playing
      after the unlock; the client decides when to leave it. */
  onContinue: () => void | Promise<void>;
}) {
  const [state, setState] = useState<
    | { phase: "loading" }
    | { phase: "error"; issue: ActionIssue }
    | { phase: "ready"; video: VideoDetailDto }
  >({ phase: "loading" });
  const [runtime, setRuntime] = useState<VideoRuntime>({
    currentTime: 0,
    duration: 0,
    playing: false,
    muted: false,
    volume: 1,
    fullscreen: false,
    lockHint: false,
    showResumeNote: false
  });
  const [unlockReady, setUnlockReady] = useState(false);
  // F115: the media element refused the file. This is NOT the `error` phase
  // above - that one belongs to the /api/video read; here the config loaded
  // fine and the FILE is what cannot be played, so the player stays on screen
  // with a sentence over it.
  const [mediaFailed, setMediaFailed] = useState(false);
  // F150: the server refused a watch report for a reason a repeat cannot
  // clear. Two separate facts, on purpose: `progressIssue` is the SENTENCE the
  // player owes the viewer, `reportingHaltedRef` is the decision to stop
  // sending. The gate refusal sets only the second - the impersonation bar
  // already names it and offers the switch, so a band here would be the same
  // refusal printed twice, 200 px apart.
  const [progressIssue, setProgressIssue] = useState<ActionIssue | null>(null);
  const reportingHaltedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const maxWatchedRef = useRef(0);
  const completedRef = useRef(false);
  const unlockReadyRef = useRef(false);
  const unlockAtRef = useRef<number | null>(null);
  const endedRef = useRef(false);
  // Only a session that actually PLAYED may report: a mere page visit must
  // never overwrite the stored resume position (evidence-run finding).
  const hasPlayedRef = useRef(false);
  const lastPostRef = useRef(0);
  const postInFlightRef = useRef(false);
  const lockHintTimer = useRef<number | null>(null);
  const resumeAppliedRef = useRef(false);

  const load = useCallback(async () => {
    setState({ phase: "loading" });
    try {
      const response = await fetch(`/api/video/${props.videoKey}`, { credentials: "include" });
      if (!response.ok) {
        setState({ phase: "error", issue: await actionIssueFrom(response) });
        return;
      }
      const body = (await response.json()) as VideoResponse;
      maxWatchedRef.current = body.video.maxWatchedSeconds;
      completedRef.current = body.video.completed;
      unlockReadyRef.current = body.video.unlockReady;
      unlockAtRef.current = body.video.unlockAtSeconds;
      resumeAppliedRef.current = false;
      // A successful re-read means the session can talk to the server again
      // (the 409 path lands here too), so the halt and its sentence go.
      reportingHaltedRef.current = false;
      setProgressIssue(null);
      setUnlockReady(body.video.unlockReady);
      setRuntime((previous) => ({
        ...previous,
        currentTime: body.video.positionSeconds,
        duration: body.video.durationSeconds ?? 0,
        showResumeNote: !body.video.completed && body.video.positionSeconds > 3
      }));
      setState({ phase: "ready", video: body.video });
    } catch {
      setState({ phase: "error", issue: actionIssueFromThrown() });
    }
  }, [props.videoKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const postProgress = useCallback(async (options?: { ended?: boolean; keepalive?: boolean }) => {
    const media = videoRef.current;
    if (!media || !hasPlayedRef.current) return;
    // F150: a refusal a repeat cannot clear is not worth repeating every ten
    // seconds. Under the read-only gate that repeat also re-fired the bar's
    // alert on a loop; under a real refusal it was a request the server was
    // always going to say no to.
    if (reportingHaltedRef.current) return;
    // One report at a time keeps the unlock-crossing tick from stacking
    // duplicates; the end-of-video report always goes through (it resets the
    // resume position and must not be lost to an in-flight interval post).
    if (postInFlightRef.current && options?.ended !== true) return;
    postInFlightRef.current = true;
    lastPostRef.current = Date.now();
    try {
      const response = await fetch(`/api/video/${props.videoKey}/progress`, {
        method: "POST",
        credentials: "include",
        keepalive: options?.keepalive === true,
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken },
        body: JSON.stringify({
          position: Number.isFinite(media.currentTime) ? media.currentTime : 0,
          duration: Number.isFinite(media.duration) && media.duration > 0 ? media.duration : null,
          ended: options?.ended === true
        })
      });
      if (response.status === 409) {
        // The server refused the report as implausible: this player is out of
        // sync with the stored truth (another device watched further, or the
        // clock drifted). Re-read the state instead of arguing.
        await load();
        return;
      }
      if (!response.ok) {
        // F150: the server NAMES its refusals, and this used to be a bare
        // `return` that threw the name away. See `progressReportRefusal`.
        const refusal = progressReportRefusal(response.status, await readErrorCode(response));
        if (refusal.halt) reportingHaltedRef.current = true;
        if (refusal.issue) setProgressIssue(refusal.issue);
        return;
      }
      const body = (await response.json()) as VideoProgressResponse;
      maxWatchedRef.current = Math.max(maxWatchedRef.current, body.maxWatchedSeconds);
      const newlyCompleted = body.completed && !completedRef.current;
      completedRef.current = body.completed;
      unlockAtRef.current = body.unlockAtSeconds;
      if (body.unlockReady && !unlockReadyRef.current) {
        unlockReadyRef.current = true;
        setUnlockReady(true);
      }
      if (newlyCompleted) {
        setState((previous) => previous.phase === "ready"
          ? { phase: "ready", video: { ...previous.video, completed: true, maxWatchedSeconds: maxWatchedRef.current } }
          : previous);
      } else if (body.completed || maxWatchedRef.current > 0) {
        setState((previous) => previous.phase === "ready"
          ? { phase: "ready", video: { ...previous.video, maxWatchedSeconds: maxWatchedRef.current } }
          : previous);
      }
    } catch {
      // A dropped report is recovered by the next tick; watching continues.
    } finally {
      postInFlightRef.current = false;
    }
  }, [props.videoKey, props.csrfToken, load]);

  // Report cadence: every ~10 s while playing, plus pause, end, tab hide and
  // the moment playback crosses the unlock threshold (the button must appear
  // when the video says so, not on the next scheduled report).
  useEffect(() => {
    if (state.phase !== "ready") return;
    const timer = window.setInterval(() => {
      const media = videoRef.current;
      if (!media || media.paused) return;
      const current = media.currentTime;
      if (!media.seeking && current > maxWatchedRef.current) maxWatchedRef.current = current;
      setRuntime((previous) => ({ ...previous, currentTime: current, duration: Number.isFinite(media.duration) && media.duration > 0 ? media.duration : previous.duration }));
      const crossedUnlock = !unlockReadyRef.current
        && unlockAtRef.current !== null
        && maxWatchedRef.current >= unlockAtRef.current;
      if (crossedUnlock || Date.now() - lastPostRef.current >= PROGRESS_POST_INTERVAL_MS) void postProgress();
    }, 500);
    const onHide = () => {
      if (document.visibilityState === "hidden") void postProgress({ keepalive: true });
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [state.phase, postProgress]);

  useEffect(() => {
    const onFullscreen = () => setRuntime((previous) => ({ ...previous, fullscreen: document.fullscreenElement === stageRef.current }));
    document.addEventListener("fullscreenchange", onFullscreen);
    return () => document.removeEventListener("fullscreenchange", onFullscreen);
  }, []);

  function showLockHint() {
    setRuntime((previous) => ({ ...previous, lockHint: true }));
    if (lockHintTimer.current) window.clearTimeout(lockHintTimer.current);
    lockHintTimer.current = window.setTimeout(() => {
      setRuntime((previous) => ({ ...previous, lockHint: false }));
    }, 2600);
  }

  function applySeek(target: number) {
    const media = videoRef.current;
    if (!media) return;
    const clamped = clampSeekTarget({
      target,
      maxWatched: maxWatchedRef.current,
      completed: completedRef.current,
      duration: Number.isFinite(media.duration) ? media.duration : 0
    });
    media.currentTime = clamped.time;
    setRuntime((previous) => ({ ...previous, currentTime: clamped.time }));
    if (clamped.blocked) showLockHint();
  }

  if (state.phase === "loading") {
    return <div className="video-gate video-gate-loading" aria-busy="true"><div className="loading-pulse" /></div>;
  }
  if (state.phase === "error") {
    return (
      <div className="video-gate video-gate-error">
        <p className="message error-message" role="alert">{props.t(actionIssueLocaleKey(state.issue))}</p>
        {/* A refusal a repeat cannot clear must not offer a repeat: the
            sentence already names the way out (N03). */}
        {!actionIssuePermanent(state.issue) && (
          <button className="secondary-button compact-button" type="button" onClick={() => void load()}>{props.t("common.retry")}</button>
        )}
      </div>
    );
  }

  const video = state.video;
  return (
    <VideoSurface
      video={{
        title: video.title,
        maxWatchedSeconds: maxWatchedRef.current,
        completed: video.completed,
        unlockAtSeconds: unlockAtRef.current
      }}
      runtime={runtime}
      unlockReady={unlockReady}
      mediaFailed={mediaFailed}
      progressIssue={progressIssue}
      onReloadMedia={() => {
        const media = videoRef.current;
        if (!media) return;
        setMediaFailed(false);
        // A fresh load of the SAME address: an expired signed URL or a dropped
        // network is exactly what a repeat can clear. If it cannot, onError
        // fires again and the sentence comes back with the partner in it.
        media.load();
      }}
      t={props.t}
      stageRef={stageRef}
      onTogglePlay={() => {
        const media = videoRef.current;
        if (!media) return;
        if (media.paused) void media.play();
        else media.pause();
      }}
      onSeek={(clientX, bar) => {
        const rect = bar.getBoundingClientRect();
        const media = videoRef.current;
        const duration = media && Number.isFinite(media.duration) ? media.duration : runtime.duration;
        if (!duration || rect.width <= 0) return;
        applySeek(((clientX - rect.left) / rect.width) * duration);
      }}
      onSeekBy={(delta) => {
        const media = videoRef.current;
        if (media) applySeek(media.currentTime + delta);
      }}
      onToggleMute={() => {
        const media = videoRef.current;
        if (!media) return;
        media.muted = !media.muted;
        setRuntime((previous) => ({ ...previous, muted: media.muted }));
      }}
      onVolume={(value) => {
        const media = videoRef.current;
        if (!media) return;
        media.volume = value;
        media.muted = value === 0;
        setRuntime((previous) => ({ ...previous, volume: value, muted: media.muted }));
      }}
      onToggleFullscreen={() => {
        if (document.fullscreenElement === stageRef.current) void document.exitFullscreen();
        else void stageRef.current?.requestFullscreen();
      }}
      onContinue={() => void props.onContinue()}
    >
      {/* No native controls: the custom bar below is the only seek surface,
          so the first-view forward-lock cannot be walked around in the UI. */}
      <video
        ref={videoRef}
        className="video-element"
        src={video.url}
        preload="metadata"
        playsInline
        controls={false}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onClick={() => {
          const media = videoRef.current;
          if (!media) return;
          if (media.paused) void media.play();
          else media.pause();
        }}
        // F115: a media failure is an EVENT the element reports; without this
        // handler the file's 403 lived only in `video.error` and nothing on
        // the page ever read it.
        onError={() => setMediaFailed(true)}
        onLoadedMetadata={() => {
          const media = videoRef.current;
          setMediaFailed(false);
          if (!media) return;
          const duration = Number.isFinite(media.duration) && media.duration > 0 ? media.duration : 0;
          if (!resumeAppliedRef.current && !video.completed && video.positionSeconds > 3 && duration > 0
            && video.positionSeconds < duration - 2) {
            media.currentTime = video.positionSeconds;
          }
          resumeAppliedRef.current = true;
          setRuntime((previous) => ({ ...previous, duration: duration || previous.duration, currentTime: media.currentTime }));
        }}
        onPlay={() => {
          endedRef.current = false;
          hasPlayedRef.current = true;
          setRuntime((previous) => ({ ...previous, playing: true, showResumeNote: false }));
        }}
        onPause={() => {
          setRuntime((previous) => ({ ...previous, playing: false }));
          if (!endedRef.current) void postProgress();
        }}
        onSeeking={() => {
          const media = videoRef.current;
          if (!media || completedRef.current) return;
          const limit = maxWatchedRef.current + FORWARD_SEEK_TOLERANCE_SECONDS;
          if (media.currentTime > limit) {
            media.currentTime = limit;
            showLockHint();
          }
        }}
        onEnded={() => {
          endedRef.current = true;
          const media = videoRef.current;
          if (media && Number.isFinite(media.duration) && media.duration > maxWatchedRef.current) {
            maxWatchedRef.current = media.duration;
          }
          setRuntime((previous) => ({ ...previous, playing: false, currentTime: previous.duration }));
          void postProgress({ ended: true });
        }}
      />
    </VideoSurface>
  );
}
